import { Box } from "@mui/material";
import { StickyNote2Outlined as NoteIcon } from "@mui/icons-material";
import { useState } from "react";
import { Card } from "../ui/primitives";
import { v2 } from "../ui/v2Tokens";
import { useAutoGrowTextarea } from "../ui/useAutoGrowTextarea";
import NoteVisibility from "./NoteVisibility";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { energyText } from "../ui/nutritionUnits";

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** absent means the trainee sees it */
  visible?: boolean;
  onVisibilityChange: (visible: boolean) => void;
};

/**
 * A note attached to the whole menu — hydration, a rest-day instruction, anything that
 * isn't a food. Shown for both menu types, since a macro-goals menu needs it just as much.
 *
 * Opt-in, like a meal's note: most menus don't carry one, and an empty textarea at the foot
 * of every menu is a permanent invitation to write something that doesn't need writing. It
 * opens automatically when the menu already has a note.
 *
 * The paper tone is deliberate — a note is the one thing on this page written by a person
 * rather than computed, and it is the only place the design uses it.
 */
const MenuNotes = ({ value, onChange, visible, onVisibilityChange }: Props) => {
  const mode = useNutritionDisplayMode();
  const [open, setOpen] = useState(Boolean(value));
  const growRef = useAutoGrowTextarea(value);

  if (!open) {
    return (
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
          height: "46px",
          borderRadius: v2.rLg,
          border: `1.5px dashed ${v2.line}`,
          background: "transparent",
          color: v2.ink3,
          fontSize: "13.5px",
          fontWeight: 500,
          fontFamily: "inherit",
          cursor: "pointer",
          transition: "all .14s",
          "&:hover": { borderColor: "#D9C88E", color: "#8A6A17", background: "#FFFDF6" },
        }}
      >
        <NoteIcon sx={{ fontSize: 16 }} />
        הוסף הערה כללית לתפריט
      </Box>
    );
  }

  return (
    <Card data-testid="menu-notes" sx={{ padding: "12px 16px 14px", background: "#FFFDF6" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <NoteIcon sx={{ fontSize: 14, color: "#8A6A17" }} />
        <Box sx={{ fontSize: "12px", fontWeight: 500, color: "#8A6A17" }}>הערה למתאמן</Box>
        <Box sx={{ fontSize: "11.5px", color: v2.ink4 }}>מוצגת עם התפריט כולו</Box>
        <Box
          component="button"
          type="button"
          onClick={() => {
            onChange("");
            setOpen(false);
          }}
          sx={{
            marginInlineStart: "auto",
            border: "none",
            background: "transparent",
            color: v2.ink4,
            fontSize: "12px",
            fontFamily: "inherit",
            padding: "3px 7px",
            borderRadius: "6px",
            cursor: "pointer",
            "&:hover": { background: "#F5EDD8", color: v2.over },
          }}
        >
          הסר
        </Box>
      </Box>
      <Box
        component="textarea"
        ref={growRef}
        aria-label="הערה למתאמן"
        placeholder={`לדוגמה: לשתות 3 ליטר מים ביום · ביום מנוחה אפשר להוריד ${energyText(300, mode)} מהפחמימות`}
        rows={2}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        sx={{
          width: "100%",
          boxSizing: "border-box",
          // the box grows with the note (see useAutoGrowTextarea), so there is nothing to scroll
          // to and nothing to drag: a hand-resized height would only fight the measured one
          resize: "none",
          overflow: "hidden",
          minHeight: "60px",
          padding: "9px 11px",
          borderRadius: v2.rSm,
          border: "1px solid #EADFC0",
          background: v2.surface,
          fontSize: "13.5px",
          fontFamily: "inherit",
          lineHeight: 1.6,
          color: v2.ink,
          outline: "none",
          "&::placeholder": { color: v2.ink4 },
          "&:focus": { borderColor: "#D9C88E", boxShadow: "0 0 0 3px rgba(217,200,142,.25)" },
        }}
      />
      <NoteVisibility
        visible={visible}
        onChange={onVisibilityChange}
        label="מוצגת למתאמן בראש התפריט"
        ariaLabel="הצג את הערת התפריט למתאמן"
      />
    </Card>
  );
};

export default MenuNotes;
